import type { TemplateFormSchemaType } from "@/schemas/template.schema";
import type { Template } from "@/common/types/templates";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { t } from "i18next";

import { HeadingEditor } from "@/components/forms/HeadingEditor";
import { type FieldErrors, FormProvider, useForm } from "react-hook-form";
import { LabeledSwitch } from "@/components/forms/Switch";
import { toast } from "@/hooks/use-toast";
import { SubscriptionOnlyButton } from "../SubscriptionOnlyButton/SubscriptionOnlyButton";
import { ValidationSuscriptionButton } from "@/common/enums/enums";

export type TemplateFormProps = {
  template?: Template;
  submitText: string;
  onSubmit: (data: TemplateFormSchemaType) => void;
  onCancel: () => void;
};

export const TemplateForm = (props: TemplateFormProps): JSX.Element => {
  const { template, submitText, onSubmit, onCancel } = props;

  const form = useForm<TemplateFormSchemaType>({
    defaultValues: {
      name: template?.name ?? "",
      headers: template?.headers ?? [],
      isDefault: !!template?.isDefault,
      optionalText: template?.optionalText ?? "",
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleError = (_: FieldErrors<TemplateFormSchemaType>): void => {
    toast({
      variant: "error",
      title: t("templates.createTemplateForm.toast.error.title"),
      description: t("templates.createTemplateForm.toast.error.description"),
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, handleError)}>
        <Card>
          <CardContent className="space-y-6 pt-6">
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("templates.createTemplateForm.templateNameLabel")}
              </Label>
              <Input
                required
                id="templateName"
                placeholder={t(
                  "templates.createTemplateForm.templateNamePlaceholder"
                )}
                {...form.register("name")}
              />
            </div>

            <HeadingEditor name="headers" />

            <div className="space-y-2">
              <Label htmlFor="instructions">
                {t("templates.createTemplateForm.instructionsLabel")}
              </Label>
              <Textarea
                className="min-h-[120px]"
                id="instructions"
                placeholder={t(
                  "templates.createTemplateForm.instructionsPlaceholder"
                )}
                {...form.register("optionalText")}
              />
            </div>

            <LabeledSwitch
              className="space-x-2"
              name="isDefault"
              labelText={t(
                "templates.createTemplateForm.setDefaultCheckboxLabel"
              )}
            />
          </CardContent>

          <CardFooter className="flex justify-end *:ml-2">
            <SubscriptionOnlyButton
              disabled={!form.formState.isValid}
              size="lg"
              type="submit"
              validations={[ValidationSuscriptionButton.PLAN_FREE_TRIAL]}
            >
              {submitText}
            </SubscriptionOnlyButton>
            <Button size="lg" variant={"outline"} onClick={onCancel}>
              {t("templates.createTemplateForm.cancelButton")}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </FormProvider>
  );
};
